import { Badge, Box, Button, HStack, Image, Text, VStack } from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import { type Opportunity, type ScrapedPropertySummary } from "../../services/sourcingApi"
import { titleizeAddress } from "../../utils/propertyUtils"
import { formatCurrency, formatPayback, formatRoi, getRoiColor } from "../../utils/formatters"
import { CardDivider, CardMetric } from "../cards/CardParts"

export interface OpportunityCardProps {
  opportunity: Opportunity
  onMarkInterested: (id: number) => void
  isMarkingInterested: boolean
}

export const getListingStatusBadge = (status: string) => {
  switch (status) {
    case "for_sale":
      return { colorPalette: "green", label: "For Sale" }
    case "under_offer":
      return { colorPalette: "orange", label: "Under Offer" }
    case "sold_stc":
      return { colorPalette: "red", label: "Sold STC" }
    case "sold":
      return { colorPalette: "red", label: "Sold" }
    default:
      return { colorPalette: "gray", label: "Unknown" }
  }
}

export const formatAddedDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000)
  if (diffDays === 0) return "added today"
  if (diffDays === 1) return "added yesterday"
  if (diffDays < 7) return `added ${diffDays} days ago`
  return `added on ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
}

export const getOpportunityPropertyDetailLine = (
  scrapedProperty: ScrapedPropertySummary
): string =>
  [
    scrapedProperty.bedrooms != null ? `${scrapedProperty.bedrooms} bed` : null,
    scrapedProperty.propertyType || null,
    scrapedProperty.floorAreaSqm ? `${Math.round(parseFloat(scrapedProperty.floorAreaSqm))} m²` : null,
  ]
    .filter(Boolean)
    .join(" · ") || "Details unavailable"

const OpportunityCard = ({
  opportunity,
  onMarkInterested,
  isMarkingInterested,
}: OpportunityCardProps) => {
  const navigate = useNavigate()
  const { scrapedProperty } = opportunity
  const { home } = scrapedProperty
  const statusBadge = getListingStatusBadge(opportunity.listingStatus)
  const isInterested = opportunity.kanbanStage === "interested"
  const roiColor = getRoiColor(home.upgradesRoi)

  const imageUrl = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    ? `https://maps.googleapis.com/maps/api/streetview?size=400x300&location=${encodeURIComponent(scrapedProperty.address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    : null

  const handleCardClick = () => {
    navigate(`/home/${home.id}?opportunityId=${opportunity.id}`)
  }

  const handleMarkInterested = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isInterested) {
      onMarkInterested(opportunity.id)
    }
  }

  const propertyDetailLine = getOpportunityPropertyDetailLine(scrapedProperty)

  return (
    <Box
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor="gray.100"
      cursor="pointer"
      transition="all 0.15s"
      _hover={{ boxShadow: "md", borderColor: "green.200", transform: "translateY(-1px)" }}
      onClick={handleCardClick}
    >
      {/* Desktop */}
      <HStack gap={0} align="stretch" display={{ base: "none", md: "flex" }}>
        <Box w="160px" minW="160px" flexShrink={0} bg="gray.100" overflow="hidden">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={`Street view of ${titleizeAddress(scrapedProperty.address)}`}
              w="100%"
              h="100%"
              minH="90px"
              objectFit="cover"
              onError={(e) => { e.currentTarget.style.display = "none" }}
            />
          ) : (
            <Box w="100%" h="100%" minH="90px" bg="gray.100" />
          )}
        </Box>

        <VStack align="start" justify="center" gap={1} flex={1} px={5} py={4}>
          <Text fontWeight="semibold" fontSize="md" color="gray.900" lineClamp={1}>
            {titleizeAddress(scrapedProperty.address)}
          </Text>
          <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
          <HStack gap={2} mt={1} flexWrap="wrap">
            <Badge colorPalette={statusBadge.colorPalette} variant="subtle" fontSize="xs">
              {statusBadge.label}
            </Badge>
            {scrapedProperty.price && (
              <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                {formatCurrency(scrapedProperty.price)}
              </Text>
            )}
            <Text fontSize="xs" color="gray.400">{formatAddedDate(opportunity.createdAt)}</Text>
          </HStack>
        </VStack>

        <HStack gap={0} align="center" pr={5} flexShrink={0}>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Green ROI"
              value={
                <Text fontSize="lg" fontWeight="semibold" color={roiColor}>
                  {formatRoi(home.upgradesRoi)}
                </Text>
              }
            />
          </Box>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Upgrade Cost"
              value={
                <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                  {formatCurrency(home.upgradesTotalCost)}
                </Text>
              }
            />
          </Box>
          <CardDivider />
          <Box px={5}>
            <CardMetric
              label="Payback"
              value={
                <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                  {formatPayback(home.upgradesPaybackPeriod)}
                </Text>
              }
            />
          </Box>
          <CardDivider />
          <Box pl={5}>
            <Button
              colorPalette="green"
              variant={isInterested ? "solid" : "outline"}
              size="sm"
              onClick={handleMarkInterested}
              disabled={isInterested || isMarkingInterested}
            >
              {isInterested ? "Interested" : "Mark as Interested"}
            </Button>
          </Box>
        </HStack>
      </HStack>

      {/* Mobile */}
      <VStack align="stretch" gap={0} display={{ base: "flex", md: "none" }}>
        {imageUrl && (
          <Image
            src={imageUrl}
            alt={`Street view of ${titleizeAddress(scrapedProperty.address)}`}
            w="100%"
            h="180px"
            objectFit="cover"
            onError={(e) => { e.currentTarget.style.display = "none" }}
          />
        )}
        <VStack align="stretch" gap={3} p={4}>
          <VStack align="start" gap={0.5}>
            <Text fontWeight="semibold" fontSize="md" color="gray.900">
              {titleizeAddress(scrapedProperty.address)}
            </Text>
            <Text fontSize="sm" color="gray.500">{propertyDetailLine}</Text>
            <HStack gap={2} mt={1} flexWrap="wrap">
              <Badge colorPalette={statusBadge.colorPalette} variant="subtle" fontSize="xs">
                {statusBadge.label}
              </Badge>
              {scrapedProperty.price && (
                <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                  {formatCurrency(scrapedProperty.price)}
                </Text>
              )}
              <Text fontSize="xs" color="gray.400">{formatAddedDate(opportunity.createdAt)}</Text>
            </HStack>
          </VStack>
          <HStack gap={6} flexWrap="wrap">
            <CardMetric
              label="Green ROI"
              value={
                <Text fontSize="md" fontWeight="semibold" color={roiColor}>
                  {formatRoi(home.upgradesRoi)}
                </Text>
              }
            />
            <CardMetric
              label="Upgrade Cost"
              value={
                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                  {formatCurrency(home.upgradesTotalCost)}
                </Text>
              }
            />
            <CardMetric
              label="Payback"
              value={
                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                  {formatPayback(home.upgradesPaybackPeriod)}
                </Text>
              }
            />
          </HStack>
          <Button
            colorPalette="green"
            variant={isInterested ? "solid" : "outline"}
            size="sm"
            onClick={handleMarkInterested}
            disabled={isInterested || isMarkingInterested}
            alignSelf="flex-start"
          >
            {isInterested ? "Interested" : "Mark as Interested"}
          </Button>
        </VStack>
      </VStack>
    </Box>
  )
}

export default OpportunityCard
